// External party portals — Buyer "Payments & ledger" (PR-A) reads. Party-
// scoped, read-only projection over the converged servicing ledger: a multi-
// loan roll-up + per-loan drill-in. Every money figure comes from the engine's
// existing ledger; the portal only displays it.

import { portalApi } from "./client";
import type { BuyerStage } from "../dashboard/journey";

/** "current" | "overdue" | "paid-off" — the loan's headline state. */
export type BuyerLoanStatus = string;

export interface BuyerFinanceLoanRow {
  loanId: string;
  loanNumber: string;
  originalPrincipal: number;
  outstandingBalance: number;
  /** Principal paid down, 0..1. */
  percentPaid: number;
  totalReceived: number;
  nextDueDate: string | null;
  scheduledMonthlyPayment: number;
  /** BLA Core §2.4 — what actually leaves the borrower's account each month:
   *  the scheduled P&I plus the payment administration fee, debited together.
   *  Optional on the wire (the DTO-extension convention) and equal to
   *  `scheduledMonthlyPayment` on a loan without the fee, so a missing value
   *  falls back to it safely. */
  totalMonthlyDebit?: number;
  daysDelinquent: number;
  isOverdue: boolean;
  status: BuyerLoanStatus;
}

export interface BuyerFinanceSummary {
  loanCount: number;
  totalOriginalPrincipal: number;
  totalOutstandingBalance: number;
  totalPaidToDate: number;
  overallPercentPaid: number;
  /** Soonest-due loan's date + payment amount. */
  nextPaymentDueDate: string | null;
  nextPaymentAmount: number;
  anyOverdue: boolean;
  overallStatus: BuyerLoanStatus;
  loans: BuyerFinanceLoanRow[];
  /** First-Payment Reconciliation (Section C) — still-unconsumed boarding
   *  reconciliation across the buyer's loans (never in the outstanding balance).
   *  Optional on the FE type; real API responses always include them. */
  closingCreditRemaining?: number;
  priorBalanceRemaining?: number;
}

export interface BuyerFinancePeriod {
  periodIndex: number;
  dueDate: string;
  expectedAmount: number;
  expectedInterest: number;
  expectedPrincipal: number;
  receivedAmount: number;
  receivedDate: string | null;
  principalBalanceAfter: number | null;
  isBalloon: boolean;
  /** ServicingPeriodStatus vocabulary: on-time / late / partial / missed /
   *  nsf / prepaid / upcoming. */
  status: string;
  /** First-Payment Reconciliation (Section C) — this period's reconciliation
   *  effect, separate from expectedAmount. Optional on the FE type. */
  creditApplied?: number;
  priorBalanceCarried?: number;
}

export interface BuyerFinanceLoanLedger {
  loanId: string;
  loanNumber: string;
  originalPrincipal: number;
  outstandingBalance: number;
  percentPaid: number;
  totalReceived: number;
  nextDueDate: string | null;
  scheduledMonthlyPayment: number;
  daysDelinquent: number;
  isOverdue: boolean;
  status: BuyerLoanStatus;
  schedule: BuyerFinancePeriod[];
}

/** PR-E — a Posted monthly statement/bill the buyer can view read-only
 *  (TPS-approved). PR-F adds the ACH pay state (off-session; the buyer
 *  presses one button — no card form — when a bank mandate is on file).
 *  PR-F2 adds the card-pay state (cardAllowed + per-state surchargeRate). */
export interface BuyerPostedStatement {
  id: string;
  loanId: string;
  loanNumber: string;
  /** DateOnly "YYYY-MM-DD". */
  periodStart: string;
  periodEnd: string;
  amountDue: number;
  postedAt: string | null;
  /** PR-F — "Posted" (still owed) | "Paid". */
  status: string;
  /** PR-F — ACH lifecycle: Initiated | Processing | Cleared | Returned |
   *  Canceled (null before any pay attempt). */
  paymentStatus: string | null;
  paidAt: string | null;
  /** A bank mandate is on file for this loan → the Pay button shows. */
  mandateOnFile: boolean;
  bankLast4: string | null;
  /** SP-2b — the loan is enrolled in autopay; a Posted bill will be
   *  drafted automatically on its due date. */
  autopayEnrolled: boolean;
  /** PR-F2 — card payment is permitted for this loan's borrower state. */
  cardAllowed: boolean;
  /** PR-F2 — the max surcharge rate for this state as a FRACTION (e.g. 0.03
   *  = 3%); the actual surcharge is sized from the card at finalize time. */
  surchargeRate: number;
}

/** PR-F — the result of paying (or re-syncing) a posted statement off-session. */
export interface StatementPaymentResult {
  statementId: string;
  statementStatus: string;
  paymentStatus: string;
  amount: number;
  bankLast4: string | null;
  message: string;
}

/** PR-F2 — begin a card payment: the connected-account PaymentIntent context
 *  the browser confirms (publishable key + connected account; the actual
 *  surcharge is sized from the chosen card at finalize, not here). */
export interface CardPaymentBegin {
  statementId: string;
  publishableKey: string;
  /** The TPS's connected account the Elements session must be scoped to. */
  stripeAccount: string;
  amountDue: number;
  /** Ceiling surcharge rate for the borrower state, as a FRACTION. */
  maxSurchargeRate: number;
}

/** PR-F2 — finalize a card payment once the card is known: the exact surcharge
 *  (0 for debit/bank-style cards), the total to charge, and the client secret
 *  the browser confirms. */
export interface CardPaymentFinalize {
  clientSecret: string;
  amountDue: number;
  /** Sized from the actual card; 0 when not surcharged (debit). */
  surcharge: number;
  total: number;
  /** Stripe card funding type: credit | debit | prepaid | unknown. */
  funding: string;
}

// ── SP-2b — buyer Home tab + autopay ────────────────────────────────────────

/** The hero's headline state — what the buyer should feel/do right now. */
export type BuyerHomeHeroState =
  | "all-set"
  | "due"
  | "past-due"
  | "processing"
  | "autopay-scheduled";

/** The current statement the hero talks about (null when nothing is owed). */
export interface BuyerHomeStatement {
  id: string;
  loanId: string;
  loanNumber: string;
  amountDue: number;
  dueDate: string | null;
  periodStart: string;
  periodEnd: string;
  postedAt: string | null;
}

/** A pending/assessed fee (e.g. a late fee) the past-due hero explains. */
export interface BuyerHomePendingFee {
  amount: number;
  feeType: string;
  /** true = assessed (will appear on the next statement); false = pending review. */
  approved: boolean;
}

/** WS-Buyer — pre-boarding (stage="application") deal context. The buyer has no
 *  loan yet, so Home shows where their application stands instead of a payment
 *  hero. `stageLabel` is the buyer-friendly stage rendering (the raw pipeline
 *  enum strings never cross the wire). Null once the buyer has a boarded loan. */
export interface BuyerHomeApplication {
  dealId: string;
  dealNumber: string;
  businessName: string | null;
  stageLabel: string;
  dealCount: number;
}

/** WS-Buyer — one recent schedule period for the Home payment-history mini-view. */
export interface BuyerHomeActivity {
  dueDate: string;
  expectedAmount: number;
  receivedAmount: number;
  receivedDate: string | null;
  status: string;
}

/** WS-Buyer — the representative (hero) loan's at-a-glance figures for the
 *  servicing stages (balance / payoff progress / next payment + a short recent
 *  activity slice). Derived server-side from the already-built ledger; null in
 *  the application stage (no loan to show). */
export interface BuyerHomeLoanSnapshot {
  loanId: string;
  loanNumber: string;
  originalPrincipal: number;
  outstandingBalance: number;
  /** Principal paid down, 0..1. */
  percentPaid: number;
  scheduledMonthlyPayment: number;
  nextDueDate: string | null;
  status: string;
  recentActivity: BuyerHomeActivity[];
}

/** WS-C — one linked loan in the Home picker payload. The optional at-a-glance
 *  figures feed the "Your loan" multi-loan switcher; they are reused from an
 *  already-built servicing ledger (never a fresh sim), so a non-hero loan whose
 *  ledger wasn't built this request carries id + number only. */
export interface BuyerHomeLoan {
  loanId: string;
  loanNumber: string;
  outstandingBalance?: number | null;
  scheduledMonthlyPayment?: number | null;
  /** BLA Core §2.4 — the combined debit (P&I plus the payment administration
   *  fee). Null under the same conditions as `scheduledMonthlyPayment`. */
  totalMonthlyDebit?: number | null;
  nextDueDate?: string | null;
  /** Principal paid down, 0..1. */
  percentPaid?: number | null;
  status?: string | null;
}

export interface BuyerHome {
  heroState: BuyerHomeHeroState;
  /** WS-Buyer — the buyer's lifecycle stage. Drives the journey spine and the
   *  stage-adaptive next-action so Home never offers an unavailable action. */
  stage: BuyerStage;
  statement: BuyerHomeStatement | null;
  /** ACH lifecycle of the current statement's payment (null = none). */
  paymentStatus: string | null;
  autopayEnrolled: boolean;
  /** When autopay will draft the current statement (autopay-scheduled). */
  autopayDraftDate: string | null;
  mandateOnFile: boolean;
  bankLast4: string | null;
  /** When nothing is owed — the expected next statement date. */
  nextExpectedDueDate: string | null;
  pendingFee: BuyerHomePendingFee | null;
  pastDueDays: number | null;
  /** WS-Buyer — pre-boarding deal context (stage="application"); null otherwise. */
  application: BuyerHomeApplication | null;
  /** WS-Buyer — the representative loan's at-a-glance snapshot; null in the
   *  application stage. */
  snapshot: BuyerHomeLoanSnapshot | null;
  /** The buyer's linked loans — feeds the autopay loan picker and the "Your
   *  loan" multi-loan switcher (so the tab never needs a second fetch). */
  loans: BuyerHomeLoan[] | null;
}

export interface BuyerAutopayNextDraft {
  statementId: string;
  amount: number;
  draftDate: string;
}

/** Per-loan autopay enrollment status + the consent text to agree to. */
export interface BuyerAutopayStatus {
  loanId: string;
  enrolled: boolean;
  status: "Active" | "Canceled" | "Suspended" | null;
  mandateOnFile: boolean;
  bankLast4: string | null;
  /** Version tag of disclosureText — echoed back on enroll so consent is
   *  always to the exact text that was shown. */
  consentVersion: string;
  disclosureText: string;
  nextDraft: BuyerAutopayNextDraft | null;
  consentAcceptedAt: string | null;
  canceledAt: string | null;
  suspendedAt: string | null;
  suspendReason: string | null;
}

/** Stripe SetupIntent context for the bank-connect step (same shape the
 *  staff AchMandateCard consumes — connected-account scoped). */
export interface BuyerAutopayMandateBegin {
  setupIntentId: string;
  clientSecret: string;
  publishableKey: string;
  stripeAccount: string;
}

// SP-4 WS-3 — a borrower-audience VFI invoice: a service fee from a
// regulated vendor, billed to the borrower through Phoenix. Deliberately a
// separate shape (and separate copy) from the buyer's loan statements — a
// service fee is NOT the monthly payment.
export interface PortalBorrowerInvoice {
  id: string;
  invoiceNumber: string;
  vendorName: string;
  amount: number;
  description: string | null;
  status: string;
  issuedAt: string | null;
  dueDate: string | null;
  paidAt: string | null;
  paymentMethod: string | null;
  payments?: { amount: number; receivedAt: string; method: string }[] | null;
}

export interface BorrowerInvoicePaymentIntent {
  paymentIntentId: string;
  clientSecret: string;
  publishableKey: string;
  status: string;
  amount: number;
}

export const portalBuyerFinanceApi = {
  /** Cross-loan roll-up across the buyer's linked loans. */
  summary: () => portalApi.get<BuyerFinanceSummary>("/buyer/finance/summary"),
  /** One linked loan's full ledger (404 when not linked to this buyer). */
  loan: (loanId: string) =>
    portalApi.get<BuyerFinanceLoanLedger>(`/buyer/finance/loans/${loanId}`),
  /** On-demand monthly account-activity PDF blob (period = YYYY-MM). The
   *  buyer surface calls this "account activity" (WS-8); the transport path
   *  is unchanged. */
  statement: (loanId: string, period: string) =>
    portalApi.getBlob(`/buyer/finance/loans/${loanId}/statement`, { period }),
  /** PR-E — the buyer's Posted statements/bills across all linked loans. */
  postedStatements: () =>
    portalApi.get<BuyerPostedStatement[]>("/buyer/finance/statements"),
  /** PR-E — one Posted statement's rendered PDF blob (the approved artifact). */
  statementPdf: (statementId: string) =>
    portalApi.getBlob(`/buyer/finance/statements/${statementId}/pdf`),
  /** PR-F — pay a posted statement off-session via the loan's bank mandate. */
  payStatement: (statementId: string) =>
    portalApi.post<StatementPaymentResult>(
      `/buyer/finance/statements/${statementId}/pay`,
    ),
  /** PR-F — re-sync a statement's payment status from Stripe (404 when
   *  there's no payment to sync). */
  syncStatementPayment: (statementId: string) =>
    portalApi.post<StatementPaymentResult>(
      `/buyer/finance/statements/${statementId}/payment-sync`,
    ),
  /** PR-F2 — begin a card payment (deferred Payment Element): returns the
   *  connected-account context the browser mounts Stripe against. */
  beginCardPayment: (statementId: string) =>
    portalApi.post<CardPaymentBegin>(
      `/buyer/finance/statements/${statementId}/card-intent`,
    ),
  /** PR-F2 — finalize once the card's payment method is created: sizes the
   *  surcharge from the actual card and returns the client secret to confirm. */
  finalizeCardPayment: (statementId: string, paymentMethodId: string) =>
    portalApi.post<CardPaymentFinalize>(
      `/buyer/finance/statements/${statementId}/card-finalize`,
      { paymentMethodId },
    ),
  // SP-2b — buyer Home tab + autopay.
  /** The Home tab's single payload: hero state + journey stage + autopay flags. */
  home: () => portalApi.get<BuyerHome>("/buyer/finance/home"),
  /** Stamp the "reviewed your loan & statement" ui flag. Returns the updated home. */
  markChecklistReviewed: () =>
    portalApi.post<BuyerHome>("/buyer/finance/home/checklist/reviewed"),
  /** Per-loan autopay status + the consent text to show before enrolling. */
  autopayStatus: (loanId: string) =>
    portalApi.get<BuyerAutopayStatus>(`/buyer/finance/loans/${loanId}/autopay`),
  /** Begin the bank-connect step (a Stripe SetupIntent — no charge). */
  beginAutopayMandate: (loanId: string) =>
    portalApi.post<BuyerAutopayMandateBegin>(
      `/buyer/finance/loans/${loanId}/autopay/mandate`,
    ),
  /** Re-sync the mandate from Stripe after the browser confirms it. */
  autopayMandateSync: (loanId: string) =>
    portalApi.post<BuyerAutopayStatus>(
      `/buyer/finance/loans/${loanId}/autopay/mandate-sync`,
    ),
  /** Enroll in autopay; consentVersion must match the disclosure shown
   *  (400 when the text changed — re-fetch and show it again). */
  enrollAutopay: (loanId: string, consentVersion: string) =>
    portalApi.post<BuyerAutopayStatus>(
      `/buyer/finance/loans/${loanId}/autopay/enroll`,
      { consentVersion },
    ),
  /** Turn autopay off — effective immediately for anything not yet drafted. */
  cancelAutopay: (loanId: string) =>
    portalApi.post<BuyerAutopayStatus>(
      `/buyer/finance/loans/${loanId}/autopay/cancel`,
    ),
  // SP-4 WS-3 — service-fee invoices (regulated vendors' fees billed
  // through Phoenix). Paid on the PHOENIX Stripe rails via a Payment
  // Element — never the statement/autopay/TPS-clearing path.
  /** Every service-fee invoice across the buyer's linked loans. */
  serviceFeeInvoices: () =>
    portalApi.get<PortalBorrowerInvoice[]>("/buyer/finance/invoices"),
  /** One invoice's detail (stamps first-open server-side). */
  serviceFeeInvoice: (invoiceId: string) =>
    portalApi.get<PortalBorrowerInvoice>(`/buyer/finance/invoices/${invoiceId}`),
  /** Create/reuse the invoice's PaymentIntent (Phoenix account). */
  createInvoicePaymentIntent: (invoiceId: string) =>
    portalApi.post<BorrowerInvoicePaymentIntent>(
      `/buyer/finance/invoices/${invoiceId}/pay`,
    ),
  /** Post-confirm sync so a card payment shows paid without the webhook. */
  syncInvoicePayment: (invoiceId: string) =>
    portalApi.post<PortalBorrowerInvoice>(
      `/buyer/finance/invoices/${invoiceId}/payment-sync`,
    ),
};
